// @ts-nocheck
import { useEffect, useState, useCallback } from 'react';

/**
 * useTileFlash — yellow brand-flash animation for tile-level updates.
 *
 * The "page is alive" feeling on the playbook depends on this: when the
 * customer edits a field inline, OR when Orbit updates a field via chat,
 * the relevant tile briefly glows yellow so the eye lands on what changed.
 *
 * Two ways to trigger:
 *   1. The component owning the tile calls `flash()` after a save succeeds
 *   2. A `window.dispatchEvent(new CustomEvent('orbit:tile-update', { detail: { section } }))`
 *      from anywhere — this hook auto-listens when given a `section` to match.
 *
 * The animation runs ~1.6s; the hook resets `flashing` to false after.
 *
 * Returns:
 *   flashing   — boolean, true while the animation is running
 *   flash()    — imperative trigger from local state (e.g. after EditableField save)
 *   flashStyle — drop-in style object the consumer can spread onto the tile
 *
 * The keyframes are registered globally on first use so any tile can adopt
 * this without extra setup.
 */

const KEYFRAMES_ID = 'tile-flash-keyframes';
const FLASH_DURATION_MS = 1600;

if (typeof document !== 'undefined' && !document.getElementById(KEYFRAMES_ID)) {
  const s = document.createElement('style');
  s.id = KEYFRAMES_ID;
  s.textContent = `
    @keyframes tile-flash {
      0%   { box-shadow: 0 0 0 0 rgba(255,234,0,0.45), 0 0 0 0 rgba(24,25,29,0.18); }
      30%  { box-shadow: 0 0 0 6px rgba(255,234,0,0.25), 0 0 0 14px rgba(24,25,29,0.10); }
      100% { box-shadow: 0 0 0 0 rgba(255,234,0,0), 0 0 0 0 rgba(24,25,29,0); }
    }
  `;
  document.head.appendChild(s);
}

export function useTileFlash(section?: string) {
  const [flashing, setFlashing] = useState(false);

  const flash = useCallback(() => {
    setFlashing(true);
    const t = setTimeout(() => setFlashing(false), FLASH_DURATION_MS);
    return () => clearTimeout(t);
  }, []);

  // Auto-listen for section-targeted events. If no section is given, the
  // hook stays manual-only (caller's flash() is the only trigger).
  useEffect(() => {
    if (!section) return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      // Match either an exact section match OR `*` (broadcast).
      const target = String(detail.section || '');
      if (target === section || target === '*') flash();
    };
    window.addEventListener('orbit:tile-update', handler as EventListener);
    return () => window.removeEventListener('orbit:tile-update', handler as EventListener);
  }, [section, flash]);

  const flashStyle: React.CSSProperties = flashing
    ? { animation: `tile-flash ${FLASH_DURATION_MS}ms ease-out` }
    : {};

  return { flashing, flash, flashStyle };
}

/**
 * Imperative dispatch helper — anywhere in the app, fire a tile update event.
 * Use this from chat-driven update paths (OrbitDock) so each tile listening
 * for its `section` flashes when its data has changed upstream.
 */
export function dispatchTileUpdate(section: string, meta?: Record<string, any>) {
  try {
    window.dispatchEvent(new CustomEvent('orbit:tile-update', {
      detail: { section, ...(meta || {}) },
    }));
  } catch { /* noop */ }
}
